import { ListsEditor, ListType } from "@prezly/slate-lists";
import markdownParser from "remark-parse";
import { remarkToSlate, slateToRemark } from "remark-slate-transformer";
import stringify from "remark-stringify";
import type { Descendant } from "slate";
import { Editor, Element, Node, Range, Transforms } from "slate";
import { HistoryEditor } from "slate-history";
import { ReactEditor } from "slate-react";
import { unified } from "unified";
import type { Node as unistNode, Parent } from "unist";
import { visit } from "unist-util-visit";

import type { ICustomElement } from "./slateTypes";
import { NodeTypes } from "./slateTypes";

export const LIST_TYPES = ["numbered-list", "bulleted-list"];

const isMacOS = navigator.platform.toUpperCase().includes("MAC");

const key = isMacOS ? "Cmd" : "Ctrl";

export const shortcuts = [
  {
    key: `${key} + /`,
    description: "Show shortcuts",
  },
  {
    key: `${key} + Z`,
    description: "Undo",
  },
  {
    key: `${key} + Shift + Z`,
    description: "Redo",
  },
  {
    key: `${key} + 0`,
    description: "Apply normal text style",
  },
  {
    key: `${key} + Alt + [1-6]`,
    description: "Apply heading style [1-6]",
  },
  {
    key: `${key} + B`,
    description: "Bold",
  },
  {
    key: `${key} + I`,
    description: "Italic",
  },
  {
    key: `${key} + Shift + 8`,
    description: "Bulleted list",
  },
  {
    key: `${key} + Shift + 7`,
    description: "Numbered list",
  },
  {
    key: `${key} + K`,
    description: "Insert or edit link",
  },
];

const toChildren = (content: string) => [{ text: content }];

export class Helpers {
  static rewriteType = (children: any): any => {
    if (Array.isArray(children)) {
      return children.map(item => Helpers.rewriteType(item));
    } else if (typeof children === "object" && children !== null) {
      const newObj = { ...children };
      if (newObj.type === NodeTypes.PARAGRAPH) {
        newObj.type = NodeTypes.LIST_ITEM_TEXT;
      }
      if (newObj.children) {
        newObj.children = Helpers.rewriteType(newObj.children);
      }
      return newObj;
    } else {
      return children;
    }
  };

  static getListType = (editor: Editor) => {
    const { selection } = editor;
    if (!selection) return;
    const [match] = Array.from(
      Editor.nodes(editor, {
        match: (n: any) => {
          return (
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            (n as any).type === "list-item"
          );
        },
      }),
    );
    if (match) {
      const parent = Node.parent(editor, match[1]) as Element;
      return parent.type;
    }
    return;
  };

  static isBlockActive = (editor: Editor, format: string, depth?: number) => {
    const { selection } = editor;
    if (!selection) return false;
    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n: any) =>
          !Editor.isEditor(n) &&
          Element.isElement(n) &&
          (n as any).type === format &&
          (n as any).depth === depth,
      }),
    );
    return !!match;
  };

  static getActiveBlock = (editor: Editor) => {
    const { selection } = editor;
    if (!selection) return;
    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n: any) => !Editor.isEditor(n) && Element.isElement(n),
      }),
    );
    return match;
  };

  static toggleList = (editor: Editor, format: NodeTypes) => {
    const listType = Helpers.getListType(editor);
    if (!listType) {
      ListsEditor.wrapInList(
        editor as any,
        format === NodeTypes.ORDERED_LIST
          ? ListType.ORDERED
          : ListType.UNORDERED,
      );
      return;
    }
    if (listType !== format) {
      ListsEditor.setListType(
        editor as any,
        format === NodeTypes.ORDERED_LIST
          ? ListType.ORDERED
          : ListType.UNORDERED,
      );
      return;
    }
    ListsEditor.unwrapList(editor as any);
  };

  static toggleBlock = (editor: Editor, format: string, depth?: number) => {
    const isActive = Helpers.isBlockActive(editor, format, depth);
    const activeBlock = Helpers.getActiveBlock(editor);
    const { selection } = editor;
    if (
      activeBlock &&
      (activeBlock[0] as Element).type === NodeTypes.BLOCK_CODE &&
      format !== NodeTypes.BLOCK_CODE
    ) {
      return;
    }
    if (
      (activeBlock &&
        (activeBlock[0] as Element).type === NodeTypes.ORDERED_LIST) ||
      format === NodeTypes.UNORDERED_LIST
    ) {
      return;
    }
    if (format === NodeTypes.BLOCK_CODE) {
      if (isActive) {
        Transforms.unwrapNodes(editor, {
          match: n =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            (n as any).type === NodeTypes.BLOCK_CODE,
        });
        Transforms.setNodes<ICustomElement>(editor, {
          type: NodeTypes.PARAGRAPH,
        });
      } else {
        if (
          activeBlock &&
          (activeBlock[0] as Element).type === NodeTypes.PARAGRAPH
        ) {
          Transforms.setNodes(editor, {
            type: NodeTypes.CODE_LINE,
          });
          Transforms.wrapNodes(editor, {
            type: NodeTypes.BLOCK_CODE,
            children: [],
          });
        } else {
          if (activeBlock && selection) {
            const anchor = Range.isBackward(selection)
              ? selection.focus
              : selection.anchor;
            Transforms.select(editor, anchor);
            const match = (n: any) =>
              !Editor.isEditor(n) &&
              Element.isElement(n) &&
              (n as any).type === (activeBlock[0] as Element).type;
            Transforms.splitNodes(editor, { match });
            Transforms.insertNodes(
              editor,
              {
                type: NodeTypes.BLOCK_CODE,
                children: [
                  { type: NodeTypes.CODE_LINE, children: [{ text: "" }] },
                ],
              },
              { match },
            );
          }
        }
      }
      return;
    }
    if (format === NodeTypes.BLOCK_QUOTE) {
      if (isActive) {
        Transforms.unwrapNodes(editor, {
          match: n => {
            return (
              !Editor.isEditor(n) &&
              Element.isElement(n) &&
              (n as any).type === NodeTypes.BLOCK_QUOTE
            );
          },
          split: true,
        });
      } else {
        Transforms.wrapNodes(editor, {
          type: NodeTypes.BLOCK_QUOTE,
          children: [],
        });
      }
      return;
    }
    const newProperties = {
      type: isActive ? "paragraph" : format,
      depth: format === NodeTypes.HEADING && !isActive ? depth : undefined,
    };
    Transforms.setNodes<ICustomElement>(editor, newProperties);

    ReactEditor.focus(editor);
  };
  static toCodeLines = (content: string): Element[] => {
    return content
      .split("\n")
      .map(line => ({ type: NodeTypes.CODE_LINE, children: toChildren(line) }));
  };
  static isMarkActive = (editor: Editor, format: string) => {
    const marks: { [key: string]: boolean } | null = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  };

  static toggleMark = (editor: Editor, format: string) => {
    const isActive = Helpers.isMarkActive(editor, format);
    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  };

  static markdown2slate(markdown: string) {
    const processor = unified()
      .use(markdownParser)
      .use(remarkToSlate, {
        overrides: {
          blockquote: (node: any, next) => {
            return {
              type: NodeTypes.BLOCK_QUOTE,
              children: node.children.length
                ? next(node.children)
                : [{ type: NodeTypes.PARAGRAPH, children: [{ text: "" }] }],
            };
          },
          link: (node: any, next) => {
            return {
              type: NodeTypes.LINK,
              url: node.url,
              children: node.children.length
                ? next(node.children)
                : [{ text: "" }],
            };
          },
          heading: (node: any, next) => {
            return {
              type: NodeTypes.HEADING,
              depth: node.depth,
              children: node.children.length
                ? next(node.children)
                : [{ text: "" }],
            };
          },
          list: (node: any, next) => {
            return {
              type: node.ordered
                ? NodeTypes.ORDERED_LIST
                : NodeTypes.UNORDERED_LIST,
              children: next(node.children),
            };
          },
          listItem: (node: any, next) => {
            return {
              type: NodeTypes.LIST_ITEM,
              children: node.children.length
                ? Helpers.rewriteType(next(node.children))
                : [
                    {
                      type: NodeTypes.LIST_ITEM_TEXT,
                      children: [{ text: "" }],
                    },
                  ],
            };
          },
          code: (node: any) => {
            return {
              ...node,
              type: NodeTypes.BLOCK_CODE,
              children: Helpers.toCodeLines(node.value),
            };
          },
        },
      });
    return processor.processSync(markdown).result;
  }

  static slate2markdown(slate: Descendant[]) {
    const processor = unified().use(stringify);
    const ast = processor.runSync(
      slateToRemark(slate, {
        overrides: {
          blockquote: (node: any, next) => {
            return {
              type: "blockquote",
              children: [{ type: "paragraph", children: next(node.children) }],
            };
          },
          [NodeTypes.ORDERED_LIST]: (node: any, next) => {
            return {
              type: "list",
              ordered: true,
              children: next(node.children),
            };
          },
          [NodeTypes.UNORDERED_LIST]: (node: any, next) => {
            return {
              type: "list",
              ordered: false,
              children: next(node.children),
            };
          },
          [NodeTypes.LIST_ITEM]: (node: any, next) => {
            return {
              type: "listItem",
              children: next(node.children),
            };
          },
          [NodeTypes.LIST_ITEM_TEXT]: (node: any, next) => {
            return {
              type: "paragraph",
              children: next(node.children),
            };
          },
          [NodeTypes.BLOCK_CODE]: (node: any, next) => {
            return {
              type: "code",
              children: next(node.children),
              value: node.children
                .map((child: any) => child.children[0].text)
                .join("\n"),
            };
          },
          [NodeTypes.CODE_LINE]: (node: any, next) => {
            return {
              type: "paragraph",
              children: next(node.children),
            };
          },
        },
      }),
    );
    const text = processor.stringify(ast as any);
    return text.trim().replace("&#x20;", " ");
  }

  static unwrapLink = (editor: Editor) => {
    Transforms.unwrapNodes(editor, {
      match: n =>
        !Editor.isEditor(n) &&
        Element.isElement(n) &&
        (n as any).type === "link",
    });
  };

  static wrapLink = (editor: Editor, url: string) => {
    if (Helpers.isBlockActive(editor, NodeTypes.LINK)) {
      Helpers.unwrapLink(editor);
    }

    const { selection } = editor;
    const isCollapsed = selection && Range.isCollapsed(selection);
    const link = {
      type: NodeTypes.LINK,
      url,
      children: isCollapsed ? [{ text: url }] : [],
    };
    if (isCollapsed) {
      Transforms.insertNodes(editor, link);
    } else {
      Transforms.wrapNodes(editor, link, { split: true });
      Transforms.collapse(editor, { edge: "end" });
    }
  };

  static prepareLink = (editor: Editor, setUrl: (url: string) => void) => {
    const isLinkActive = Helpers.isBlockActive(editor, NodeTypes.LINK);
    if (isLinkActive) {
      const [link] = Editor.nodes(editor, {
        match: n => Element.isElement(n) && n.type === NodeTypes.LINK,
      });
      if (link) {
        setUrl((link[0] as Element).url || "");
      }
    } else {
      setUrl("");
    }
    HistoryEditor.withoutSaving(editor, () => {
      Editor.addMark(editor, "prepareLink", true);
    });
  };

  static isUrl = (url: string) => {
    return /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/.test(
      url,
    );
  };

  static isElement = (editor: Editor, element: NodeTypes) => {
    const [match] = Editor.nodes(editor, {
      match: (n: any) => n.type === element,
    });
    return !!match;
  };
  static insertParagraph = (editor: Editor, element: NodeTypes) => {
    Transforms.insertNodes(
      editor,
      { type: NodeTypes.PARAGRAPH, children: [{ text: "" }] },
      {
        match: n => (n as any).type === element,
      },
    );
  };
}
